import { ConsentCheckbox } from '@/components/react/form/elements/ConsentCheckbox';
import { PhoneNumberInput } from '@/components/react/form/elements/PhoneNumberInput';
import { SelectDropdown } from '@/components/react/form/elements/SelectDropdown';
import { SubmitButton } from '@/components/react/form/elements/SubmitButton';
import { TextArea } from '@/components/react/form/elements/TextArea';
import { TextInput } from '@/components/react/form/elements/TextInput';
import { getSubmitFormValues } from '@/components/react/form/lib/getFormFormatedValues';
import {
  submitContactForm,
  submitGatedContentForm,
} from '@/components/react/form/lib/submitForm';
import type {
  ContactFormValues,
  GatedContentFormValues,
  MetaValues,
} from '@/schemas/components/molecules/formSchema';
import type { AstroLocale } from '@/schemas/components/shared/i18n';
import { gatedContentStore } from '@/stores/gatedContent';
import { createFormHook, createFormHookContexts } from '@tanstack/react-form';
import { useMutation } from '@tanstack/react-query';

export const { fieldContext, useFieldContext, formContext, useFormContext } =
  createFormHookContexts();

export const { useAppForm, withForm } = createFormHook({
  fieldComponents: {
    ConsentCheckbox,
    PhoneNumberInput,
    SelectDropdown,
    TextArea,
    TextInput,
  },
  formComponents: {
    SubmitButton,
  },
  fieldContext,
  formContext,
});

export const useContactFormQuery = (
  language: AstroLocale,
  thankYouPage?: string,
) =>
  useMutation({
    mutationFn: ({
      value,
      meta,
    }: {
      value: ContactFormValues;
      meta: MetaValues;
    }) => submitContactForm(value, meta, language, language === 'zh'),
    onSuccess: (_, params) => {
      const redirect = () => {
        window.location.assign(`${window.location.origin}${thankYouPage}`);
      };

      if (window?.ChiliPiper && language !== 'zh') {
        const leadValues = getSubmitFormValues(
          params.value,
          params.meta,
          language,
        );

        window.ChiliPiper.submit(
          import.meta.env.PUBLIC_CHILLI_PIPER_TENANT_ID,
          import.meta.env.PUBLIC_CHILLI_PIPER_ROUTER_NAME,
          {
            map: true,
            lead: leadValues,
            onSuccess: redirect(),
            onClose: redirect(),
          },
        );
        return;
      }

      redirect();
    },
  });

export const useGatedContentFormQuery = () =>
  useMutation({
    mutationFn: ({ value }: { value: GatedContentFormValues }) =>
      submitGatedContentForm(value),
    onSuccess: (_, params) => {
      window.dataLayer.push({ event: 'form_submit_gated_content' });

      const currentPagePath = window.location.pathname;
      const currentStore = gatedContentStore.get();
      const currentSubmittedPages = currentStore.submittedPages || [];

      const updatedSubmittedPages =
        currentSubmittedPages.includes(currentPagePath) ?
          currentSubmittedPages
        : [...currentSubmittedPages, currentPagePath];

      gatedContentStore.set({
        ...currentStore,
        formSubmitted: 'true',
        submittedPages: updatedSubmittedPages,
        formSubmissionData: {
          ...params.value,
        },
        pdfDownloaded: 'false',
      });
    },
  });
